import { buildQuery, type QueryFilter } from './query'
import type { Labeled, Tone } from './tone'

export type DealOutcome = 'open' | 'won' | 'lost'
export type ClosedOutcome = Exclude<DealOutcome, 'open'>
export type DealChannel = 'portal' | 'site' | 'whatsapp' | 'instagram' | 'manual' | 'other'
export type FunnelType = 'sale' | 'rent'
export type StageKey = 'unattended' | 'in_service' | 'visit_scheduled' | 'visit_done' | 'proposal' | 'formalization'
export type VisitStatus = 'scheduled' | 'done' | 'canceled' | 'no_show'

const DAY_MS = 86_400_000
const HOUR_MS = 3_600_000
const MINUTE_MS = 60_000

export const DEAL_OUTCOME: Record<DealOutcome, Labeled> = {
  open: { label: 'Em aberto', tone: 'progress' },
  won: { label: 'Ganho', tone: 'success' },
  lost: { label: 'Perdido', tone: 'danger' }
}

export const dealOutcomeOf = (value?: string | null): DealOutcome => (value === 'won' || value === 'lost' ? value : 'open')

export const dealOutcome = (value?: string | null): Labeled => DEAL_OUTCOME[dealOutcomeOf(value)]

export const isDealClosed = (deal: { outcome?: string | null }): boolean => dealOutcomeOf(deal.outcome) !== 'open'

export const DEAL_CHANNELS: { value: DealChannel; label: string }[] = [
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'site', label: 'Site' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'portal', label: 'Portais' },
  { value: 'manual', label: 'Cadastro manual' },
  { value: 'other', label: 'Outros' }
]

export const ENTRY_RULE_CHANNELS: DealChannel[] = ['whatsapp', 'site', 'instagram', 'portal']

const CHANNEL_HINT: Record<DealChannel, string> = {
  whatsapp: 'Mensagens no WhatsApp, inclusive as atendidas pela IA',
  site: 'Chat, formulários e atendente IA do site',
  instagram: 'Mensagens no Instagram, inclusive as atendidas pela IA',
  portal: 'OLX, ZAP e Viva Real',
  manual: 'Negócios criados pela equipe no painel',
  other: 'Origens sem canal conhecido'
}

export const isDealChannel = (value?: string | null): value is DealChannel => DEAL_CHANNELS.some(option => option.value === value)

export const dealChannelLabel = (channel?: string | null): string =>
  DEAL_CHANNELS.find(option => option.value === channel)?.label ?? DEAL_CHANNELS[DEAL_CHANNELS.length - 1].label

export const dealChannelHint = (channel: DealChannel): string => CHANNEL_HINT[channel]

const ORIGIN_CHANNEL: Record<string, DealChannel> = {
  olx: 'portal',
  mcmv_olx: 'portal',
  zap: 'portal',
  vivareal: 'portal',
  site: 'site',
  marketplace: 'site',
  'site-ai': 'site',
  'web-ai': 'site',
  whatsapp: 'whatsapp',
  'whatsapp-ai': 'whatsapp',
  instagram: 'instagram',
  'instagram-ai': 'instagram',
  manual: 'manual'
}

export function channelOfOrigin(origin?: string | null): DealChannel {
  const key = origin?.trim().toLowerCase()
  return (key && ORIGIN_CHANNEL[key]) || 'other'
}

export const dealChannelOf = (deal: { channel?: string | null; origin?: string | null }): DealChannel =>
  isDealChannel(deal.channel) ? deal.channel : channelOfOrigin(deal.origin)

export interface DealReason {
  key: string
  label: string
}

export const LOST_REASONS: DealReason[] = [
  { key: 'no_contact', label: 'Sem contato' },
  { key: 'stopped_responding', label: 'Parou de responder' },
  { key: 'no_show', label: 'Não compareceu à visita' },
  { key: 'over_budget', label: 'Fora do orçamento' },
  { key: 'bought_elsewhere', label: 'Comprou com concorrente' },
  { key: 'financing_denied', label: 'Financiamento negado' },
  { key: 'gave_up', label: 'Desistiu' },
  { key: 'other', label: 'Outro' }
]

export const WON_REASONS: DealReason[] = [
  { key: 'contract_signed', label: 'Contrato assinado' },
  { key: 'proposal_accepted', label: 'Proposta aceita' },
  { key: 'other', label: 'Outro' }
]

export const OTHER_REASON = 'other'

export const DEAL_NOTE_MAX = 500

export const dealReasonsOf = (outcome: ClosedOutcome): DealReason[] => (outcome === 'won' ? WON_REASONS : LOST_REASONS)

export function dealReasonLabel(outcome: string | null | undefined, reason?: string | null): string {
  if (!reason) return ''
  const reasons = outcome === 'won' ? WON_REASONS : outcome === 'lost' ? LOST_REASONS : [...LOST_REASONS, ...WON_REASONS]
  return reasons.find(item => item.key === reason)?.label ?? reason
}

export const reasonNeedsNote = (reason?: string | null): boolean => reason === OTHER_REASON

export function closeDealProblem(
  outcome: ClosedOutcome,
  reason: string,
  note: string,
  reasons: DealReason[] = dealReasonsOf(outcome)
): string | null {
  if (!reason) return outcome === 'won' ? 'Escolha o motivo do ganho' : 'Escolha o motivo da perda'
  if (!reasons.some(item => item.key === reason)) return 'Motivo inválido'
  if (reasonNeedsNote(reason) && !note.trim()) return 'Descreva o motivo'
  if (note.length > DEAL_NOTE_MAX) return `Use no máximo ${DEAL_NOTE_MAX} caracteres`
  return null
}

export const FUNNEL_TYPES: { value: FunnelType; label: string }[] = [
  { value: 'sale', label: 'Venda' },
  { value: 'rent', label: 'Locação' }
]

export const funnelTypeLabel = (type?: string | null): string => FUNNEL_TYPES.find(option => option.value === type)?.label ?? 'Venda'

export const funnelTypeHint = (funnel: { name: string; type?: string | null }): string | null => {
  const label = funnelTypeLabel(funnel.type)
  return funnel.name.trim().localeCompare(label, 'pt-BR', { sensitivity: 'base' }) === 0 ? null : label
}

export const funnelOptionLabel = (funnel: { name: string; type?: string | null }): string => {
  const hint = funnelTypeHint(funnel)
  return hint ? `${funnel.name} · ${hint}` : funnel.name
}

export const FUNNEL_LIMIT = 20
export const STAGE_MIN = 2
export const STAGE_MAX = 10
export const STAGNATION_MAX_DAYS = 365

export const STAGE_KEYS: { value: StageKey; label: string; hint: string }[] = [
  { value: 'unattended', label: 'Sem atendimento', hint: 'Negócios novos esperam aqui o primeiro contato' },
  { value: 'in_service', label: 'Em atendimento', hint: 'O primeiro contato da equipe move para cá' },
  { value: 'visit_scheduled', label: 'Visita agendada', hint: 'Agendar uma visita move para cá' },
  { value: 'visit_done', label: 'Visita realizada', hint: 'Concluir uma visita move para cá' },
  { value: 'proposal', label: 'Proposta', hint: 'Registrar uma proposta move para cá' },
  { value: 'formalization', label: 'Formalização', hint: 'Etapa de contrato, sem movimento automático' }
]

export const stageKeyInfo = (key?: string | null) => STAGE_KEYS.find(option => option.value === key)

export function parseStagnationDays(raw: string): number | null | undefined {
  const text = raw.trim()
  if (text === '') return null
  if (!/^\d+$/.test(text)) return undefined
  const days = Number(text)
  return days >= 1 && days <= STAGNATION_MAX_DAYS ? days : undefined
}

const timeOf = (value?: string | null): number | null => {
  if (!value) return null
  const time = new Date(value).getTime()
  return Number.isNaN(time) ? null : time
}

export interface DealTiming {
  outcome?: string | null
  stageEnteredAt?: string | null
  lastContactAt?: string | null
  firstContactAt?: string | null
  createdAt?: string | null
  stagnant?: boolean | null
  stageDays?: number | null
}

export function dealStageDays(deal: DealTiming, now: Date = new Date()): number {
  if (typeof deal.stageDays === 'number' && Number.isFinite(deal.stageDays)) return Math.max(0, Math.floor(deal.stageDays))
  const entered = timeOf(deal.stageEnteredAt) ?? timeOf(deal.createdAt)
  return entered === null ? 0 : Math.max(0, Math.floor((now.getTime() - entered) / DAY_MS))
}

export function idleDays(deal: DealTiming, now: Date = new Date()): number {
  const entered = timeOf(deal.stageEnteredAt) ?? timeOf(deal.createdAt)
  const contact = timeOf(deal.lastContactAt)
  const since = Math.max(entered ?? 0, contact ?? 0)
  return since === 0 ? 0 : Math.max(0, Math.floor((now.getTime() - since) / DAY_MS))
}

export function isDealStagnant(deal: DealTiming, stagnationDays?: number | null, now: Date = new Date()): boolean {
  if (dealOutcomeOf(deal.outcome) !== 'open') return false
  if (typeof deal.stagnant === 'boolean') return deal.stagnant
  if (!stagnationDays) return false
  const entered = timeOf(deal.stageEnteredAt) ?? timeOf(deal.createdAt)
  const since = Math.max(entered ?? 0, timeOf(deal.lastContactAt) ?? 0)
  return since > 0 && now.getTime() - since > stagnationDays * DAY_MS
}

export function stageTimeLabel(days: number): string {
  if (days <= 0) return 'Entrou hoje'
  return days === 1 ? '1 dia na etapa' : `${days} dias na etapa`
}

export function stagnantLabel(days: number): string {
  return days <= 1 ? 'Parado há 1 dia' : `Parado há ${days} dias`
}

export function durationLabel(ms: number): string {
  const safe = Math.max(0, ms)
  if (safe < HOUR_MS) return `${Math.max(1, Math.floor(safe / MINUTE_MS))} min`
  if (safe < DAY_MS) return `${Math.floor(safe / HOUR_MS)} h`
  const days = Math.floor(safe / DAY_MS)
  return days === 1 ? '1 dia' : `${days} dias`
}

export function unattendedSince(deal: DealTiming, alertMinutes: number | null | undefined, now: Date = new Date()): number | null {
  if (alertMinutes === null || alertMinutes === undefined) return null
  if (dealOutcomeOf(deal.outcome) !== 'open' || deal.firstContactAt) return null
  const created = timeOf(deal.createdAt)
  if (created === null) return null
  const waiting = now.getTime() - created
  return waiting > alertMinutes * MINUTE_MS ? waiting : null
}

export const unattendedLabel = (waitingMs: number): string => `Sem atendimento há ${durationLabel(waitingMs)}`

export const unattendedShortLabel = (waitingMs: number): string => `${durationLabel(waitingMs)} sem atendimento`

export const UNATTENDED_ALERT_OPTIONS: { value: string; label: string }[] = [
  { value: 'off', label: 'Desligado' },
  { value: '15', label: '15 minutos' },
  { value: '30', label: '30 minutos' },
  { value: '60', label: '1 hora' },
  { value: '120', label: '2 horas' },
  { value: '240', label: '4 horas' },
  { value: '480', label: '8 horas' },
  { value: '1440', label: '1 dia' }
]

export const unattendedAlertValue = (minutes: number | null | undefined): string =>
  minutes === null || minutes === undefined ? 'off' : String(minutes)

export function unattendedAlertMinutes(value: string): number | null {
  if (value === 'off') return null
  const minutes = Number(value)
  return Number.isFinite(minutes) && minutes > 0 ? Math.round(minutes) : null
}

const BRL = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 0, maximumFractionDigits: 0 })
const BRL_CENTS = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' })
const COMPACT = new Intl.NumberFormat('pt-BR', { notation: 'compact', maximumFractionDigits: 1 })

const numberOf = (value?: number | string | null): number | null => {
  if (value === null || value === undefined || value === '') return null
  const parsed = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

export function dealValueLabel(value?: number | string | null): string {
  const amount = numberOf(value)
  if (amount === null) return 'Sem valor'
  return Number.isInteger(amount) ? BRL.format(amount) : BRL_CENTS.format(amount)
}

export function compactBRL(value?: number | string | null): string {
  const amount = numberOf(value) ?? 0
  return Math.abs(amount) < 10_000 ? BRL.format(amount) : `R$ ${COMPACT.format(amount)}`
}

export function sumDealValues(deals: { value?: number | string | null }[]): number {
  return deals.reduce((total, deal) => total + (numberOf(deal.value) ?? 0), 0)
}

export interface ColumnTotals {
  count: number | null
  totalValue?: number | null
}

export function moveBetweenTotals<T extends ColumnTotals>(source: T, destination: T, value?: number | string | null): [T, T] {
  const amount = numberOf(value) ?? 0
  return [
    { ...source, count: Math.max((source.count ?? 1) - 1, 0), totalValue: Math.max((source.totalValue ?? 0) - amount, 0) },
    { ...destination, count: (destination.count ?? 0) + 1, totalValue: (destination.totalValue ?? 0) + amount }
  ]
}

export const VISIT_STATUS: Record<VisitStatus, Labeled> = {
  scheduled: { label: 'Agendada', tone: 'info' },
  done: { label: 'Realizada', tone: 'success' },
  canceled: { label: 'Cancelada', tone: 'neutral' },
  no_show: { label: 'Não compareceu', tone: 'danger' }
}

export const visitStatus = (status?: string | null): Labeled => VISIT_STATUS[(status as VisitStatus) ?? 'scheduled'] ?? VISIT_STATUS.scheduled

export function isVisitLate(visit: { status?: string | null; scheduledAt: string }, now: Date = new Date()): boolean {
  const at = timeOf(visit.scheduledAt)
  return visit.status === 'scheduled' && at !== null && at < now.getTime()
}

export function nextVisit<T extends { status?: string | null; scheduledAt: string }>(visits: T[], now: Date = new Date()): T | null {
  const upcoming = visits
    .filter(visit => visit.status === 'scheduled' && (timeOf(visit.scheduledAt) ?? 0) >= now.getTime())
    .sort((a, b) => (timeOf(a.scheduledAt) ?? 0) - (timeOf(b.scheduledAt) ?? 0))
  return upcoming[0] ?? null
}

const pad2 = (part: number) => String(part).padStart(2, '0')

export const VISIT_TIMES: string[] = Array.from({ length: 31 }, (_, index) => {
  const minutes = 7 * 60 + index * 30
  return `${pad2(Math.floor(minutes / 60))}:${pad2(minutes % 60)}`
})

export interface VisitSlot {
  date: string
  time: string
}

export function visitSlotOf(value: Date): VisitSlot {
  const rounded = new Date(value)
  rounded.setMinutes(Math.round(rounded.getMinutes() / 30) * 30, 0, 0)
  return {
    date: `${rounded.getFullYear()}-${pad2(rounded.getMonth() + 1)}-${pad2(rounded.getDate())}`,
    time: `${pad2(rounded.getHours())}:${pad2(rounded.getMinutes())}`
  }
}

export function visitSlotIso({ date, time }: Partial<VisitSlot>): string | null {
  if (!date || !time || !/^\d{4}-\d{2}-\d{2}$/.test(date) || !/^\d{2}:\d{2}$/.test(time)) return null
  const parsed = new Date(`${date}T${time}:00`)
  return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString()
}

export function defaultVisitSlot(now: Date = new Date()): VisitSlot {
  const next = new Date(now)
  next.setDate(next.getDate() + 1)
  next.setHours(10, 0, 0, 0)
  return visitSlotOf(next)
}

export const dayMonthLabel = (plain: string): string => {
  const [, month, day] = plain.split('-')
  return day && month ? `${day}/${month}` : plain
}

export const UNASSIGNED_FILTER = 'sem-responsavel'

export interface DealsFilter {
  customerIds?: string[]
  funnelId?: string
  outcome?: DealOutcome | 'all'
  assignees?: string[]
  channels?: string[]
  realStateId?: string
  period?: [string, string] | null
  closedPeriod?: [string, string] | null
  stages?: string[]
  temperature?: { min: number; max: number } | null
}

export function dealAssigneeFilter(assignees: string[] = []): { assigneeIds: string[]; unassigned: boolean } {
  return {
    assigneeIds: assignees.filter(value => value !== UNASSIGNED_FILTER),
    unassigned: assignees.includes(UNASSIGNED_FILTER)
  }
}

export function periodBounds(period?: [string, string] | null): [string, string] | null {
  if (!period) return null
  const [from, to] = period
  const start = new Date(`${from}T00:00:00`)
  const end = new Date(`${to}T23:59:59.999`)
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null
  return [start.toISOString(), end.toISOString()]
}

export function dealsQuery(filter: DealsFilter): string | undefined {
  const { assigneeIds } = dealAssigneeFilter(filter.assignees)
  const bounds = periodBounds(filter.period)
  const closedBounds = periodBounds(filter.closedPeriod)
  const clauses: QueryFilter[] = [
    { field: 'customerId', values: filter.customerIds ?? [] },
    { field: 'funnelId', values: filter.funnelId ? [filter.funnelId] : [], operator: 'eq' },
    { field: 'statusId', values: filter.stages ?? [] },
    { field: 'outcome', values: filter.outcome && filter.outcome !== 'all' ? [filter.outcome] : [], operator: 'eq' },
    { field: 'assigneeId', values: assigneeIds },
    { field: 'channel', values: filter.channels ?? [] },
    { field: 'realStateId', values: filter.realStateId ? [filter.realStateId] : [], operator: 'eq' },
    { field: 'createdAt', values: bounds ?? [], operator: 'between' },
    { field: 'closedAt', values: closedBounds ?? [], operator: 'between' },
    {
      field: 'temperature',
      values: filter.temperature ? [String(filter.temperature.min), String(filter.temperature.max)] : [],
      operator: 'between'
    }
  ]
  return buildQuery(clauses)
}

export interface MemberLike {
  userId: string
  name?: string | null
  email?: string | null
  user?: { name?: string | null; email?: string | null } | null
}

export const memberName = (member?: MemberLike | null): string =>
  member?.name?.trim() || member?.user?.name?.trim() || member?.email?.trim() || member?.user?.email?.trim() || 'Corretor'

export function assigneeLabel(assigneeId: string | null | undefined, members: Map<string, MemberLike>, currentUserId?: string | null): string {
  if (!assigneeId) return 'Sem responsável'
  if (assigneeId === currentUserId) {
    const member = members.get(assigneeId)
    return member ? `${memberName(member)} (você)` : 'Você'
  }
  const member = members.get(assigneeId)
  return member ? memberName(member) : 'Corretor'
}

export function rotationOrder(memberIds: string[], members: MemberLike[]): MemberLike[] {
  const byId = new Map(members.map(member => [member.userId, member]))
  return memberIds.map(id => byId.get(id)).filter((member): member is MemberLike => !!member)
}

export function toggleRotationMember(memberIds: string[], userId: string): string[] {
  return memberIds.includes(userId) ? memberIds.filter(id => id !== userId) : [...memberIds, userId]
}

export function moveInList<T>(items: T[], from: number, to: number): T[] {
  if (from === to || from < 0 || to < 0 || from >= items.length || to >= items.length) return items
  const next = [...items]
  const [moved] = next.splice(from, 1)
  next.splice(to, 0, moved)
  return next
}

export interface EntryRule {
  channel: string
  funnelId: string
  statusId: string | null
}

export function setEntryRule(rules: EntryRule[], channel: string, funnelId: string | null, statusId: string | null): EntryRule[] {
  const others = rules.filter(rule => rule.channel !== channel)
  return funnelId ? [...others, { channel, funnelId, statusId }] : others
}

export function entryRuleOf(rules: EntryRule[], channel: string): EntryRule | undefined {
  return rules.find(rule => rule.channel === channel)
}

export function pruneEntryRules(rules: EntryRule[], funnels: { id: string; stages: { id: string }[] }[]): EntryRule[] {
  return rules.flatMap(rule => {
    const funnel = funnels.find(item => item.id === rule.funnelId)
    if (!funnel) return []
    return [{ ...rule, statusId: rule.statusId && funnel.stages.some(stage => stage.id === rule.statusId) ? rule.statusId : null }]
  })
}

const PERCENT = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 1 })
const DAYS = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 1 })

export function conversionLabel(rate?: number | null): string {
  if (rate === null || rate === undefined || !Number.isFinite(rate)) return '—'
  return `${PERCENT.format(rate * 100)}%`
}

export function avgDaysLabel(days?: number | null): string {
  if (days === null || days === undefined || !Number.isFinite(days)) return '—'
  if (days < 1) return 'menos de 1 dia'
  return `${DAYS.format(days)} ${days < 2 ? 'dia' : 'dias'}`
}

export const shareOf = (value: number, max: number): number => (max > 0 ? Math.min(100, Math.max(0, (value / max) * 100)) : 0)

export interface ReportSeriesPoint {
  date: string
  new: number
  won: number
  lost: number
}

export function bucketSeries(series: ReportSeriesPoint[], maxBars = 31): ReportSeriesPoint[] {
  if (series.length <= maxBars) return series
  const size = Math.ceil(series.length / maxBars)
  const buckets: ReportSeriesPoint[] = []
  for (let start = 0; start < series.length; start += size) {
    const chunk = series.slice(start, start + size)
    buckets.push({
      date: chunk[0].date,
      new: chunk.reduce((total, point) => total + point.new, 0),
      won: chunk.reduce((total, point) => total + point.won, 0),
      lost: chunk.reduce((total, point) => total + point.lost, 0)
    })
  }
  return buckets
}

export function lossReasonRows(rows: { reason: string; count: number }[]): { reason: string; label: string; count: number; share: number }[] {
  const total = rows.reduce((sum, row) => sum + row.count, 0)
  return [...rows]
    .sort((a, b) => b.count - a.count)
    .map(row => ({ ...row, label: dealReasonLabel('lost', row.reason), share: total > 0 ? row.count / total : 0 }))
}

export interface DealEventLike {
  title: string
  description?: string | null
  metadata?: { type?: string | null; value?: unknown }[] | null
}

const dealMeta = (entry: DealEventLike): Record<string, unknown> => {
  const match = entry.metadata?.find(item => item?.type === 'deal') ?? null
  const value = match?.value
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : {}
}

const textOf = (value: unknown): string | null => (typeof value === 'string' && value.trim() ? value.trim() : null)

const DATE_TIME = new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })

export function visitWhenLabel(value?: string | null): string {
  const time = timeOf(value)
  return time === null ? '' : DATE_TIME.format(new Date(time)).replace(',', ' às')
}

const AUTO_MOVE_REASON: Record<string, string> = {
  first_contact: 'primeiro contato',
  visit_scheduled: 'visita agendada',
  visit_done: 'visita realizada',
  proposal: 'proposta registrada',
  reopened: 'negócio reaberto',
  stage_removed: 'etapa removida'
}

export interface DealEventView {
  title: string
  description?: string
  tone: Tone
  icon: string
}

const DEAL_EVENT_BASE: Record<string, { title: string; tone: Tone; icon: string }> = {
  'deal-created': { title: 'Negócio criado', tone: 'progress', icon: 'Sparkles' },
  'deal-first-contact': { title: 'Primeiro atendimento', tone: 'info', icon: 'MessageCircle' },
  'deal-transferred': { title: 'Negócio transferido', tone: 'highlight', icon: 'Repeat2' },
  'deal-assigned': { title: 'Responsável definido', tone: 'highlight', icon: 'UserPlus' },
  'deal-won': { title: 'Negócio ganho', tone: 'success', icon: 'Trophy' },
  'deal-lost': { title: 'Negócio perdido', tone: 'danger', icon: 'XOctagon' },
  'deal-reopened': { title: 'Negócio reaberto', tone: 'progress', icon: 'RotateCcw' },
  'deal-proposal': { title: 'Proposta registrada', tone: 'success', icon: 'Handshake' },
  'visit-scheduled': { title: 'Visita agendada', tone: 'warning', icon: 'CalendarPlus' },
  'visit-rescheduled': { title: 'Visita reagendada', tone: 'warning', icon: 'CalendarDays' },
  'visit-done': { title: 'Visita realizada', tone: 'success', icon: 'CalendarCheck' },
  'visit-canceled': { title: 'Visita cancelada', tone: 'neutral', icon: 'CalendarX' },
  'visit-no-show': { title: 'Cliente não compareceu', tone: 'danger', icon: 'CalendarX2' },
  'status-change': { title: 'Mudou de etapa', tone: 'progress', icon: 'Workflow' }
}

export const isDealEvent = (title?: string | null): boolean => !!title && title in DEAL_EVENT_BASE

export function dealEventView(entry: DealEventLike, nameOf: (userId: string | null) => string = () => 'Corretor'): DealEventView | null {
  const base = DEAL_EVENT_BASE[entry.title]
  if (!base) return null
  const meta = dealMeta(entry)
  const parts: string[] = []
  const note = textOf(meta.note)

  switch (entry.title) {
    case 'deal-won':
    case 'deal-lost': {
      const reason = dealReasonLabel(entry.title === 'deal-won' ? 'won' : 'lost', textOf(meta.reason))
      if (reason) parts.push(`Motivo: ${reason}`)
      if (numberOf(meta.value as number | null) !== null) parts.push(dealValueLabel(meta.value as number))
      if (note) parts.push(note)
      break
    }
    case 'deal-transferred':
      parts.push(`De ${nameOf(textOf(meta.from))} para ${nameOf(textOf(meta.to))}`)
      break
    case 'deal-assigned':
      parts.push(nameOf(textOf(meta.assigneeId)))
      break
    case 'deal-created': {
      const channel = textOf(meta.channel)
      if (channel) parts.push(`Canal: ${dealChannelLabel(channel)}`)
      break
    }
    case 'deal-first-contact': {
      const channel = textOf(meta.channel)
      parts.push(channel ? `Por ${dealChannelLabel(channel)}` : 'A equipe respondeu o cliente')
      break
    }
    case 'deal-proposal':
      if (numberOf(meta.value as number | null) !== null) parts.push(dealValueLabel(meta.value as number))
      if (note) parts.push(note)
      break
    case 'status-change': {
      const from = textOf(meta.from)
      const to = textOf(meta.to)
      if (from && to) parts.push(`De ${from} para ${to}`)
      else if (to) parts.push(`Para ${to}`)
      const reason = AUTO_MOVE_REASON[textOf(meta.reason) ?? '']
      if (reason) parts.push(`Automático: ${reason}`)
      break
    }
    default: {
      const when = visitWhenLabel(textOf(meta.scheduledAt))
      if (when) parts.push(when)
      const place = textOf(meta.realStateTitle)
      if (place) parts.push(place)
      if (note) parts.push(note)
    }
  }

  const description = parts.length > 0 ? parts.join(' · ') : (textOf(entry.description) ?? undefined)
  return { ...base, description }
}

export const DEAL_ERROR_MESSAGES: Record<string, string> = {
  DEAL_DUPLICATE: 'Este cliente já tem um negócio em aberto para este imóvel.',
  DEAL_CLOSED: 'Este negócio está encerrado. Reabra para alterar.',
  DEAL_NOT_CLOSED: 'Este negócio já está em aberto.',
  DEAL_REASON_INVALID: 'Escolha um motivo da lista.',
  DEAL_NOTE_REQUIRED: 'Descreva o motivo quando escolher "Outro".',
  FUNNEL_NAME_TAKEN: 'Já existe um funil com este nome.',
  FUNNEL_LIMIT_REACHED: `A imobiliária já tem ${FUNNEL_LIMIT} funis, o máximo permitido.`,
  FUNNEL_HAS_DEALS: 'Este funil tem negócios. Mova ou encerre os negócios antes de excluir.',
  FUNNEL_MINIMUM: 'A imobiliária precisa de pelo menos um funil.',
  STAGE_LIMIT_REACHED: `Um funil pode ter no máximo ${STAGE_MAX} etapas.`,
  STAGE_KEY_TAKEN: 'Outra etapa deste funil já usa esta automação.',
  STAGE_HAS_ACTIVE_DEALS: 'Esta etapa tem negócios em aberto. Mova os negócios antes de remover.',
  STAGE_MINIMUM: `Um funil precisa de pelo menos ${STAGE_MIN} etapas.`,
  VISIT_NOT_FOUND: 'Visita não encontrada.'
}

export function conversationSearchTerms(customer?: { name?: string | null; phones?: { phone?: string | null }[] | null } | null): string[] {
  if (!customer) return []
  const phones = (customer.phones ?? [])
    .map(entry => (entry.phone ?? '').replace(/\D/g, ''))
    .filter(digits => digits.length >= 8)
    .map(digits => digits.slice(-8))
  const name = customer.name?.trim()
  return [...new Set([...phones, ...(name && name.length >= 3 ? [name] : [])])].slice(0, 4)
}

export function customerConversations<T extends { id: string; customerId?: string | null; lastMessageAt?: string | null; updatedAt?: string | null }>(
  lists: T[][],
  customerId: string
): T[] {
  const seen = new Map<string, T>()
  for (const item of lists.flat()) if (item.customerId === customerId && !seen.has(item.id)) seen.set(item.id, item)
  const time = (item: T) => timeOf(item.lastMessageAt ?? item.updatedAt ?? null) ?? 0
  return [...seen.values()].sort((a, b) => time(b) - time(a))
}
